import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, MaskedLine, usePageMeta } from "@/components/Reveal";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const SECTIONS = [
  {
    tag: "Weddings and Celebrations",
    id: "weddings",
    faqs: [
      {
        q: "What is a celebrant, and how is it different from a registrar?",
        a: "A celebrant creates a fully personalised ceremony with no legal restrictions on what can be included. A registrar conducts the legal registration. In Scotland, a celebrant with a Religious Bodies licence can also legally register the marriage.",
      },
      {
        q: "Can we write our own vows?",
        a: "Yes. I can help you write them, give you a structure to follow, or you can write them entirely yourself.",
      },
      {
        q: "Can we include Scottish poetry, readings, or traditions?",
        a: "Absolutely. Scottish storytelling and the poetic tradition are central to how I approach ceremony writing.",
      },
      {
        q: "Can family members or friends take part?",
        a: "Yes. Readings, music, symbolic rituals, and contributions from loved ones can all be woven in.",
      },
      {
        q: "How far do you travel?",
        a: "I serve Perthshire and surrounding areas. Travel further afield is considered on request.",
      },
      {
        q: "How far in advance should we book?",
        a: "As early as possible for weddings; popular dates go quickly. For other ceremonies, please get in touch as soon as you know you need someone.",
      },
      {
        q: "What happens after I enquire?",
        a: "I will get back to you promptly to arrange a no-obligation conversation. From there, we work together at your pace.",
      },
    ],
  },
  {
    tag: "Funerals and Memorials",
    id: "funerals",
    faqs: [
      {
        q: "How quickly can you respond?",
        a: "I understand that time matters. I aim to respond to all funeral enquiries within a few hours and can work to tight timescales.",
      },
      {
        q: "Does the ceremony have to be religious?",
        a: "No. I offer non-religious, spiritual, and hybrid ceremonies that reflect what the person believed and how the family wishes to remember them.",
      },
      {
        q: "Can you help us find the right words?",
        a: "Yes. You do not need to arrive with a eulogy written. I will ask gentle questions and help shape the tribute from what you share with me.",
      },
      {
        q: "Can family members speak during the ceremony?",
        a: "Yes, and I can support them in preparing what they want to say.",
      },
      {
        q: "Can you work with our funeral director?",
        a: "Yes. I am experienced in coordinating with funeral directors to ensure the day runs smoothly.",
      },
    ],
  },
];

const Faq = () => {
  usePageMeta(
    "Frequently Asked Questions | Scottish Voice and Verse Celebrancy",
    "Answers to common questions about celebrant weddings, funerals, vow renewals, and what to expect when working with Scottish Voice and Verse."
  );

  useEffect(() => {
    const data = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: SECTIONS.flatMap((s) =>
        s.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        }))
      ),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "faq-jsonld";
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById("faq-jsonld");
      if (el) el.remove();
    };
  }, []);

  return (
    <main data-testid="faq-page">
      <section className="grain relative overflow-hidden bg-forestdeep pb-20 pt-40 text-cream lg:pb-28 lg:pt-52" data-testid="faq-hero">
        <div className="mx-auto max-w-7xl px-6 sm:px-12">
          <p className="mb-6 text-[11px] uppercase tracking-[0.35em] text-gold">Questions</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl" data-testid="faq-page-heading">
            <MaskedLine delay={0.2}>Frequently Asked Questions</MaskedLine>
          </h1>
          <Reveal delay={0.5}>
            <p className="font-display mt-8 max-w-2xl text-xl italic text-cream/80 sm:text-2xl">
              Asked often, answered honestly. If your question is not here, Karen is only a message away.
            </p>
            <span className="gold-rule mt-10 block" />
          </Reveal>
        </div>
      </section>

      {SECTIONS.map((section, si) => (
        <section
          key={section.id}
          className={`py-16 lg:py-24 ${si % 2 === 1 ? "bg-creamdeep/40" : ""}`}
          data-testid={`faq-section-${section.id}`}
        >
          <div className="mx-auto max-w-4xl px-6 sm:px-12">
            <Reveal className="mb-10">
              <span
                data-testid={`faq-tag-${section.id}`}
                className="inline-block border border-gold/60 bg-creamdeep/50 px-5 py-2 text-[11px] uppercase tracking-[0.3em] text-gold"
              >
                {section.tag}
              </span>
              <span className="mt-6 block h-px w-16 bg-gold/50" />
            </Reveal>
            <Reveal>
              <Accordion type="single" collapsible>
                {section.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`${section.id}-${i}`} className="border-gold/30" data-testid={`faq-${section.id}-item-${i + 1}`}>
                    <AccordionTrigger
                      data-testid={`faq-${section.id}-question-${i + 1}`}
                      className="font-display py-6 text-lg text-forest hover:text-mulberry hover:no-underline sm:text-xl"
                    >
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent data-testid={`faq-${section.id}-answer-${i + 1}`} className="pb-6 text-sm leading-[1.9] text-slate2 sm:text-base">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </section>
      ))}

      <section className="mx-auto max-w-3xl px-6 py-24 text-center lg:py-32" data-testid="faq-cta-section">
        <Reveal>
          <span className="gold-rule mx-auto mb-10 block" />
          <h2 className="font-display text-4xl text-forest sm:text-5xl" data-testid="faq-cta-heading">
            Still wondering?
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-sm leading-[1.9] text-slate2 sm:text-base">
            The easiest way to have a question answered is to ask it directly.
          </p>
          <Link
            to="/contact"
            data-testid="faq-cta-button"
            className="group mt-10 inline-flex items-center gap-3 bg-forest px-10 py-4 text-[12px] uppercase tracking-[0.25em] text-cream transition-colors duration-500 hover:bg-mulberry"
          >
            Start your ceremony story
            <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1.5" />
          </Link>
          <p className="mt-5 text-xs italic text-slate2/70">No obligation, just a conversation.</p>
        </Reveal>
      </section>
    </main>
  );
};

export default Faq;
